"use client";

/**
 * fund-rolling-chart.tsx
 *
 * Horizontal histogram of 3-year rolling return distribution. Each bar is a
 * return bucket (e.g. "10-15%") with count of rolling windows falling into
 * it. Best bucket highlighted green, worst red, rest blue.
 *
 * Used in: src/app/funds/[slug]/page.tsx (Performance Analysis section)
 */

export interface FundRollingChartProps {
  distribution: Array<{ range: string; count: number }> | null;
  windowsAnalyzed?: number | null;
  bestPct?: number | null;
  worstPct?: number | null;
  medianPct?: number | null;
}

function isNegativeBucket(range: string): boolean {
  return range.trim().startsWith("<") || range.trim().startsWith("-");
}

function isTopBucket(range: string): boolean {
  return range.includes(">") || /2[05]\+|>25/.test(range);
}

export function FundRollingChart({
  distribution,
  windowsAnalyzed,
  bestPct,
  worstPct,
  medianPct,
}: FundRollingChartProps) {
  if (!distribution || distribution.length === 0) {
    return (
      <div className="bg-surface border border-surface-light/10 rounded-xl p-5">
        <p className="text-muted-foreground text-sm">
          Insufficient history for rolling returns analysis.
        </p>
      </div>
    );
  }

  const totalCount = distribution.reduce((s, d) => s + d.count, 0);
  const maxCount = Math.max(...distribution.map((d) => d.count), 1);

  return (
    <div className="bg-surface border border-surface-light/10 rounded-xl p-5">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
        <div>
          <div className="text-foreground text-sm font-serif">
            3-year rolling returns distribution
          </div>
          <div className="text-dim text-[11px]">
            {windowsAnalyzed ?? totalCount} rolling windows analyzed
          </div>
        </div>
      </div>

      {/* Bars */}
      <div className="space-y-2 mt-4">
        {distribution.map((bucket) => {
          const widthPct = (bucket.count / maxCount) * 100;
          const pctOfTotal = totalCount > 0 ? (bucket.count / totalCount) * 100 : 0;
          const color = isNegativeBucket(bucket.range)
            ? "#EF4444"
            : isTopBucket(bucket.range)
            ? "#10B981"
            : "#3B82F6";
          return (
            <div key={bucket.range} className="flex items-center gap-3">
              <div className="text-muted-foreground text-xs w-[70px] text-right shrink-0 font-tabular">
                {bucket.range}
              </div>
              <div className="flex-1 relative h-6 bg-surface-light/5 rounded overflow-hidden">
                <div
                  className="h-full rounded transition-all duration-700"
                  style={{
                    width: `${Math.max(widthPct, bucket.count > 0 ? 3 : 0)}%`,
                    backgroundColor: color,
                  }}
                />
              </div>
              <div className="text-foreground text-xs font-tabular w-[70px] shrink-0">
                {bucket.count} ({pctOfTotal.toFixed(0)}%)
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary stats */}
      {(bestPct !== null && bestPct !== undefined) ||
      (worstPct !== null && worstPct !== undefined) ||
      (medianPct !== null && medianPct !== undefined) ? (
        <div className="grid grid-cols-3 gap-3 mt-5 pt-4 border-t border-surface-light/10">
          <div>
            <div className="text-muted-foreground text-[10px] uppercase tracking-wide mb-1">
              Best
            </div>
            <div className="text-cgreen text-sm font-bold font-tabular">
              {bestPct !== null && bestPct !== undefined
                ? `${bestPct.toFixed(1)}%`
                : "-"}
            </div>
          </div>
          <div>
            <div className="text-muted-foreground text-[10px] uppercase tracking-wide mb-1">
              Median
            </div>
            <div className="text-foreground text-sm font-bold font-tabular">
              {medianPct !== null && medianPct !== undefined
                ? `${medianPct.toFixed(1)}%`
                : "-"}
            </div>
          </div>
          <div>
            <div className="text-muted-foreground text-[10px] uppercase tracking-wide mb-1">
              Worst
            </div>
            <div className="text-cred text-sm font-bold font-tabular">
              {worstPct !== null && worstPct !== undefined
                ? `${worstPct.toFixed(1)}%`
                : "-"}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
